import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { config } from '@app/core/app-config';
import { BillingFacade } from '@app/shared/+state/billing';
import { UsersFacade } from '@app/shared/+state/users';
import { NotificationType } from '@app/shared/interfaces';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Logger } from '@utils';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

// Define interfaces locally to avoid import issues
export interface ThreadUser {
  uid: number;
  first_name: string;
  last_name: string;
  email: string;
  has_access: boolean;
}

export interface ThreadUsersResponse {
  data: ThreadUser[];
}

export interface TributeValue {
  key: string;
  value: string;
  type: unknown;
  uid: number;
  string: string;
  firstName: string;
  lastName: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThreadNoteService {
  readonly isOnLegacyPlan = signal<boolean>(false);
  readonly hasBetaFeaturesEnabled = signal<boolean>(false);
  readonly isCostinsAccount = signal<boolean>(false);
  public teamNotesEnabled = computed(() => {
    return this.hasBetaFeaturesEnabled() && (!this.isOnLegacyPlan() || this.isCostinsAccount());
  });
  public teamNotesEnabled$ = toObservable(this.teamNotesEnabled);

  constructor(
    private http: HttpClient,
    private toast: ToastNotificationsService,
    private posthog: PosthogService,
    private userFacade: UsersFacade,
    private billingFacade: BillingFacade
  ) {
    this.userFacade.isOnLegacyPlan$.pipe(takeUntilDestroyed()).subscribe((isLegacy) => {
      this.isOnLegacyPlan.set(isLegacy);
    });
    this.userFacade.hasBetaFeaturesEnabled$.pipe(takeUntilDestroyed()).subscribe((hasBetaFeaturesEnabled) => {
      this.hasBetaFeaturesEnabled.set(hasBetaFeaturesEnabled);
    });
    this.userFacade.isUser31280$.pipe(takeUntilDestroyed()).subscribe((isCostinsAccount) => {
      this.isCostinsAccount.set(isCostinsAccount);
    });
  }

  /**
   * Creates a note for the given thread
   */
  createNote(threadUuid: string, content: string, generateAiSummary = true): Observable<unknown> {
    if (!content || content.trim() === '' || content === '<p></p>') {
      Logger.error('Cannot create note with empty content');
      return throwError(() => new Error('Note content cannot be empty'));
    }

    const url = `${config.API_URL}/threads/${threadUuid}/notes`;
    const payload: { content: string; generate_ai_thread_summary?: boolean } = { content: content.trim() };

    if (generateAiSummary) {
      payload.generate_ai_thread_summary = generateAiSummary;
    }

    return this.http.post(url, payload).pipe(
      tap(() => {
        Logger.info('Note created successfully');
      }),
      catchError((error) => {
        Logger.error('Failed to create note', error);
        this.toast.open('Failed to add note', 'Dismiss', NotificationType.Error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetches thread users and converts them to TributeValue format for mentions
   */
  getThreadUsers(threadUuid: string): Observable<ThreadUsersResponse> {
    const url = `${config.API_URL}/threads/${threadUuid}/users`;

    return this.http.get<ThreadUsersResponse>(url).pipe(
      tap((response) => {
        Logger.info('Thread users fetched successfully', response.data?.length);
      }),
      catchError((error) => {
        Logger.error('Failed to fetch thread users', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Converts thread users to TributeValue format for mentions
   */
  convertUsersToTributeValues(users: ThreadUser[]): TributeValue[] {
    return users.map((user) => ({
      key: `${user.first_name} ${user.last_name}`,
      value: `<span class="user-mention" contenteditable="false" data-uid="${user.uid}" data-first-name="${user.first_name}" data-last-name="${user.last_name}">@${user.first_name} ${user.last_name}</span>`,
      type: 'UserMention' as unknown,
      uid: user.uid,
      string: `${user.first_name} ${user.last_name}`,
      firstName: user.first_name,
      lastName: user.last_name,
    }));
  }

  /**
   * Checks if the content contains user mentions
   */
  hasUserMentions(content: string): boolean {
    if (!content) {
      return false;
    }

    const hasUserMentionClass = content.includes('class="user-mention"') || content.includes("class='user-mention'");
    const hasDataUid = content.includes('data-uid=');

    return hasUserMentionClass && hasDataUid;
  }

  /**
   * Extracts mentioned user UIDs from HTML content
   */
  extractMentionedUserUids(content: string): string[] {
    if (!content) {
      return [];
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const mentionElements = doc.querySelectorAll('.user-mention[data-uid]');

    const uids = Array.from(mentionElements)
      .map((element) => element.getAttribute('data-uid'))
      .filter((uid): uid is string => uid !== null);

    // Return unique UIDs
    return [...new Set(uids)];
  }
}
